import { nextTick, onBeforeUnmount, onMounted, type Ref } from "vue";

/** Makes Lipscore's review link open the PDP "Reviews" tab and scroll to it. */
export const REVIEW_LINK_SELECTOR =
	"#lipscore-rating a, .lipscore-rating-review-link, [data-reviews-tab-link]";

interface ScrollTarget {
	scrollIntoView: (options?: ScrollIntoViewOptions) => void;
}

/** Split out for tests. */
export const createReviewLinkClickHandler =
	(
		openReviews: () => void,
		/** Read after nextTick, so it sees the re-rendered panel. */
		getTarget: () => ScrollTarget | null | undefined,
		prefersReducedMotion: () => boolean,
		linkSelector = REVIEW_LINK_SELECTOR
	) =>
	async (event: Pick<MouseEvent, "target" | "preventDefault">) => {
		const link = (event.target as Element | null)?.closest?.(linkSelector);
		if (!link) return;
		event.preventDefault();
		openReviews();
		await nextTick();
		getTarget()?.scrollIntoView({
			behavior: prefersReducedMotion() ? "auto" : "smooth",
			block: "start"
		});
	};

/**
 * The first visible `preferSelector` match inside `root`, else `root`. A
 * mobile accordion keeps its Reviews header far below the block top, while the
 * desktop tabs hide that header (no client rects) — so one selector serves both.
 */
export const resolveScrollTarget = (
	root: HTMLElement | null | undefined,
	preferSelector?: string
): HTMLElement | null => {
	if (!root) return null;
	if (!preferSelector) return root;
	const match = root.querySelector<HTMLElement>(preferSelector);
	return match && match.getClientRects().length > 0 ? match : root;
};

interface ReviewsTabLinkOptions {
	/** Scroll here instead of `target` when it is visible, e.g. the mobile panel header. */
	preferSelector?: string;
	/** Links that open the tab; Lipscore's review links by default. */
	linkSelector?: string;
}

export const useReviewsTabLink = (
	openReviews: () => void,
	target: Ref<HTMLElement | null | undefined>,
	{ preferSelector, linkSelector }: ReviewsTabLinkOptions = {}
): void => {
	const handleClick = createReviewLinkClickHandler(
		openReviews,
		() => resolveScrollTarget(target.value, preferSelector),
		() => window.matchMedia("(prefers-reduced-motion: reduce)").matches,
		linkSelector
	);

	onMounted(() => document.addEventListener("click", handleClick, true));
	onBeforeUnmount(() =>
		document.removeEventListener("click", handleClick, true)
	);
};
